import { faDownload, faGears } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { useRecoilState, useSetRecoilState } from "recoil";
import { alertMessageState, loadingState } from "src/atom/LoadingState";
import { authState } from "src/atom/authState";
import Paginator from "src/components/Paginator";
import callApi from "src/services/Common/api";
import Swal from "sweetalert2";

export default function AdmitCard() {
  const searchObject = {
    size: 5,
    totalPages: 0,
    totalItems: 0,
    sortWith: "id",
    currentPage: 1,
    sortOrder: "desc",
    registrationNumber: "",
    status: "",
  };

  const [formData, setFormData] = useState(searchObject);
  const [candidates, setCandidates] = useState([]);
  const [sheets, setSheets] = useState([]);
  const [auth] = useRecoilState(authState);
  const enableLoader = useSetRecoilState(loadingState);
  const setAlertMessage = useSetRecoilState(alertMessageState);
  const [selectedFile, setSelectedFile] = useState(null);
  const [keyValue, setKeyValue] = useState("");

  const handleKeyChange = (e) => {
    setKeyValue(e.target.value);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (
      file &&
      (file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel")
    ) {
      setSelectedFile(file);
    } else {
      setSelectedFile(null);
    }
  };

  // Fetch admit card data
  const fetchAdmitCardData = async () => {
    enableLoader(true);
    const payload = {
      currentPage: formData.currentPage,
      size: formData.size,
      sortWith: formData.sortWith,
      sortOrder: formData.sortOrder,
      registrationNumber: formData.registrationNumber || null,
      status: formData.status || null,
      candidateName: null,
    };
    try {
      const responseData = await callApi(
        `/v1/api/admit-cards/status`,
        "POST",
        payload,
        true,
        setAlertMessage
      );
      if (responseData.success !== false && responseData.body) {
        setCandidates(responseData.body.data);
        setFormData((prev) => ({
          ...prev,
          totalPages: +responseData.body.totalPages || 0,
          totalItems: +responseData.body.totalItems || 0,
          currentPage: +responseData.body.currentPage || 1,
        }));
      } else {
        setCandidates([]);
        setAlertMessage({
          message: "No data returned from the server",
          type: "warning",
        });
      }
    } catch (error) {
      setCandidates([]);
      setAlertMessage({
        message: "Failed to fetch data",
        type: "error",
      });
    }
    enableLoader(false);
  };

   const fetchSheetData = async () => {
    enableLoader(true);
    const payload = {
      currentPage: formData.currentPage,
      size: formData.size,
      sortWith: formData.sortWith,
      sortOrder: formData.sortOrder,
      registrationNumber: formData.registrationNumber || null,
      status: formData.status || null,
      candidateName: null,
    };
    try {
      const responseData = await callApi(
        `/v1/api/admit-cards/sheet-status`,
        "POST",
        payload,
        true,
        setAlertMessage
      );
      if (responseData.success !== false && responseData.body) {
        setSheets(responseData.body.data);
        setFormData((prev) => ({
          ...prev,
          totalPages: +responseData.body.totalPages || 0,
          totalItems: +responseData.body.totalItems || 0,
          currentPage: +responseData.body.currentPage || 1,
        }));
      } else {
        setSheets([]);
        setAlertMessage({
          message: "No data returned from the server",
          type: "warning",
        });
      }
    } catch (error) {
      setCandidates([]);
      setAlertMessage({
        message: "Failed to fetch data",
        type: "error",
      });
    }
    enableLoader(false);
  };

  // Generate individual admit card
  const handleGenerateIndividual = async (e) => {
    e.preventDefault();
    Swal.fire({
      title: "Are you sure you want to generate individual admit card?",
      showDenyButton: true,
      confirmButtonText: "Yes",
      denyButtonText: `No`,
    }).then(async (result) => {
      if (result.isConfirmed) {
        if (!formData.registrationNumber) return;
        enableLoader(true);
        const responseData = await callApi(
          `/v1/api/admit-cards/generate/${formData.registrationNumber}`,
          "POST",
          null,
          true,
          setAlertMessage
        );
        if (responseData.success !== false) {
          setAlertMessage(`${responseData.message}|true`);
          fetchAdmitCardData(); // Refresh data
        }
        enableLoader(false);
      }
    });
  };

  // Download individual admit card
  const downloadIndividual = async (e) => {
    e.preventDefault();
    if (!formData.registrationNumber) return;
    enableLoader(true);
    await callApi(
      `/v1/api/admit-cards/individual/${formData.registrationNumber}`,
      "GET",
      null,
      true,
      setAlertMessage,
      true, // Indicate this is a file download
      formData.registrationNumber
    );
    enableLoader(false);
  };

  // Download multiple admit cards via Excel file
  const downloadBulk = async () => {
    if (!selectedFile) return;

    enableLoader(true);

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const response = await callApi(
        "/v1/api/admit-cards/bulk",
        "POST",
        formData,
        true,
        setAlertMessage,
        true, // isFileDownload
        null // registrationNo
      );

      if (response.success) {
        // Check for missing registrations in response
        if (
          response.missingRegistrations &&
          response.missingRegistrations.length > 0
        ) {
          setAlertMessage(
            `Some admit cards not found for: ${response.missingRegistrations.join(
              ", "
            )}`
          );
        } else {
          setAlertMessage("All admit cards downloaded successfully|true");
        }
      }
    } catch (error) {
      setAlertMessage(error.message || "Bulk download failed");
    } finally {
      enableLoader(false);
    }
  };

  // Generate all admit cards
  const handleGenerateAll = async (e) => {
    e.preventDefault();

    Swal.fire({
      title: "Are you sure you want to generate all the admit cards?",
      showDenyButton: true,
      confirmButtonText: "Yes",
      denyButtonText: `No`,
    }).then(async (result) => {
      if (result.isConfirmed) {
        enableLoader(true);
        const responseData = await callApi(
          `/v1/api/admit-cards/generate`,
          "POST",
          null,
          true,
          setAlertMessage
        );
        if (responseData.success !== false) {
          setAlertMessage(`${responseData.message}|true`);
          fetchAdmitCardData(); // Refresh data
        }
        enableLoader(false);
      }
    });
  };

  const handleGenerateAttendanceSheet = async (e) => {
    e.preventDefault();

    Swal.fire({
      title: "Are you sure you want to generate attendance sheet?",
      showDenyButton: true,
      confirmButtonText: "Yes",
      denyButtonText: `No`,
    }).then(async (result) => {
      if (result.isConfirmed) {
        enableLoader(true);
        const responseData = await callApi(
          `/v1/api/admit-cards/generate-attendance-sheet`,
          "POST",
          null,
          true,
          setAlertMessage
        );
        if (responseData.success !== false) {
          setAlertMessage(`${responseData.message}|true`);
          fetchAdmitCardData(); // Refresh data
        }
        enableLoader(false);
      }
    });
  };

  // Check encrypted key
  const checkKey = async (e) => {
    e.preventDefault();
    enableLoader(true);
    const responseData = await callApi(
      `/v1/api/admit-cards/decrypt?encryptedData=${keyValue}`,
      "GET",
      null,
      true,
      setAlertMessage,
      false,
      null,
      {},
      false
    );
    if (responseData.success !== false) {
      setAlertMessage(`${responseData.body}|true`);
      setKeyValue("");
    }
    enableLoader(false);
  };

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value })); // Reset to first page
  };

  const handleClear = (e) => {
    e.preventDefault();
    setFormData((prev) => ({
      ...prev,
      registrationNumber: "",
      status: "",
    }));
    fetchAdmitCardData();
    fetchSheetData();
    setSelectedFile(null);
    document.getElementById("excelFile").value = ""; // Clear file input
  };

  const nextPage = (pageNumber) => {
    setFormData((prev) => ({ ...prev, currentPage: pageNumber }));
  };

  // Format datetime for display
  const formatDateTime = (dateTime) => {
    if (!dateTime) return "-";
    const date = new Date(dateTime);
    return date.toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  useEffect(() => {
    fetchAdmitCardData();
    fetchSheetData();
  }, [formData.currentPage, formData.size, formData.status]);

  return (
    <>
      <div className="card">
        <div
          className="card-header text-white d-flex justify-content-between"
          style={{ backgroundColor: "#3C4B64" }}
        >
          <div>Admit Card Management</div>
          <div>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => {
                fetchAdmitCardData();
                fetchSheetData();
              }}
            >
              Refresh
            </button>
          </div>
        </div>
        <div className="card-body">
          <div className="mb-3">
            <form>
              <div className="row">
                <div className="col-md-2 mb-2">
                  {/* <label>
                    <b>Status</b>
                  </label> */}
                  <select
                    className="form-control form-control-sm"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                  >
                    <option value="">All</option>
                    <option value="COMPLETED">Completed</option>
                    <option value="IN_PROGRESS">In-Progress</option>
                    <option value="FAILED">Failed</option>
                  </select>
                </div>
                <div className="col-md-6 mb-2">
                  <div className="row">
                    <div className="col-md-6 mb-2">
                      {/* <label>
                        <b>CET Registration No.</b>
                      </label> */}
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        name="registrationNumber"
                        value={formData.registrationNumber}
                        onChange={handleInputChange}
                        placeholder="Enter CET Registration No."
                      />
                    </div>
                    <div className="col-md-6 mb-2">
                      <button
                        type="button"
                        className="btn btn-success btn-sm me-1"
                        onClick={handleGenerateIndividual}
                        disabled={!formData.registrationNumber}
                        style={{ height: "31px" }}
                      >
                        <FontAwesomeIcon icon={faGears} className="me-1" />{" "}
                        Single
                      </button>

                      <button
                        disabled={formData.registrationNumber}
                        type="button"
                        className="btn btn-primary btn-sm me-1"
                        onClick={handleGenerateAll}
                      >
                        <FontAwesomeIcon icon={faGears} className="me-1" /> All
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm me-1"
                        onClick={downloadIndividual}
                        disabled={!formData.registrationNumber}
                      >
                        <FontAwesomeIcon icon={faDownload} />{" "}
                      </button>
                      <button
                        disabled={formData.registrationNumber}
                        type="button"
                        className="btn btn-success btn-sm me-1"
                        onClick={handleGenerateAttendanceSheet}
                      >
                        <FontAwesomeIcon icon={faGears} className="me-1" />{" "}
                        Sheet
                      </button>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="row">
                    <div className="col-md-6 mb-2">
                      <input
                        type="file"
                        id="excelFile"
                        accept=".xlsx,.xls"
                        className="form-control form-control-sm me-2"
                        onChange={handleFileChange}
                      />
                    </div>
                    <div className="col-md-6 mb-2">
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm me-2"
                        onClick={downloadBulk}
                        disabled={!selectedFile}
                      >
                        <FontAwesomeIcon icon={faDownload} />{" "}
                      </button>
                      <button
                        type="button"
                        className="btn btn-warning btn-sm text-white"
                        onClick={handleClear}
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </form>
            <div className="row">
              <div className="col-sm-10 mt-2">
                <label>
                  <b>Key</b>
                </label>
                <textarea
                  rows={2}
                  className="form-control w-100"
                  style={{ resize: "none" }}
                  value={keyValue}
                  onChange={handleKeyChange}
                ></textarea>
              </div>
              <div className="col-sm-2 d-flex align-items-center mt-2">
                <button
                  type="button"
                  className="btn btn-primary w-100"
                  disabled={keyValue === ""}
                  onClick={checkKey}
                >
                  Check Key
                </button>
              </div>
            </div>
          </div>
          <div className="table-responsive">
             <h4>Admit Card Generation</h4>
            <table className="table table-striped table-bordered table-hover">
              <thead>
                <tr>
                  <th scope="col">S.No.</th>
                  <th scope="col">Start Time</th>
                  <th scope="col">End Time</th>
                  <th scope="col">Processed Count</th>
                  <th scope="col">Total Candidates</th>
                  <th scope="col">Status</th>
                </tr>
              </thead>
              <tbody>
                {candidates.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center">
                      No records found
                    </td>
                  </tr>
                ) : (
                  candidates.map((candidate, index) => (
                    <tr key={candidate.id}>
                      <td>
                        {(formData.currentPage - 1) * formData.size + index + 1}
                      </td>
                      <td>{formatDateTime(candidate.startTime)}</td>
                      <td>{formatDateTime(candidate.endTime)}</td>
                      <td>{candidate.processedCount}</td>
                      <td>{candidate.totalCandidates}</td>
                      <td>
                        <div
                          className={`bg-${
                            candidate.status === "COMPLETED"
                              ? "success"
                              : candidate.status === "FAILED"
                              ? "danger"
                              : "primary"
                          } p-1 text-white text-center`}
                          style={{ borderRadius: 10 }}
                        >
                          {candidate.status}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="col-12 d-flex justify-content-center">
            <Paginator
              pagination={formData}
              range={3}
              nextPage={nextPage}
              setPagination={setFormData}
            />
          </div>
          <div className="table-responsive">
            <h4>Sheet Generation</h4>
            <table className="table table-striped table-bordered table-hover">
              <thead>
                <tr>
                  <th scope="col">S.No.</th>
                  <th scope="col">Start Time</th>
                  <th scope="col">End Time</th>
                  <th scope="col">Processed Count</th>
                  <th scope="col">Total Candidates</th>
                  <th scope="col">Status</th>
                </tr>
              </thead>
              <tbody>
                {sheets.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center">
                      No records found
                    </td>
                  </tr>
                ) : (
                  sheets.map((candidate, index) => (
                    <tr key={candidate.id}>
                      <td>
                        {(formData.currentPage - 1) * formData.size + index + 1}
                      </td>
                      <td>{formatDateTime(candidate.startTime)}</td>
                      <td>{formatDateTime(candidate.endTime)}</td>
                      <td>{candidate.processedCount}</td>
                      <td>{candidate.totalCandidates}</td>
                      <td>
                        <div
                          className={`bg-${
                            candidate.status === "COMPLETED"
                              ? "success"
                              : candidate.status === "FAILED"
                              ? "danger"
                              : "primary"
                          } p-1 text-white text-center`}
                          style={{ borderRadius: 10 }}
                        >
                          {candidate.status}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="col-12 d-flex justify-content-center">
            <Paginator
              pagination={formData}
              range={3}
              nextPage={nextPage}
              setPagination={setFormData}
            />
          </div>
        </div>
      </div>
    </>
  );
}
